import { useMemo } from 'react';
import { Props as ApexChartProps } from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { alpha } from '@mui/material/styles';
import {
  differenceInCalendarDays,
  getDaysInMonth,
  startOfMonth,
  startOfWeek,
} from 'date-fns';

type Filters = {
  end?: string;
  timestep?: string;
};

type AxisSeries = {
  name?: string;
  type?: string;
  data?: any[];
  [key: string]: any;
};

type TransformResult = {
  series: ApexChartProps['series'];
  options: ApexOptions | undefined;
  nameMap?: Map<string, string>;
};

type YAxisUnion = NonNullable<ApexOptions['yaxis']>;
type YAxisItem = YAxisUnion extends Array<infer R> ? R : YAxisUnion;

type Params = {
  enableIncompleteHighlight?: boolean;
  filters?: Filters;
  series: ApexChartProps['series'];
  options: ApexOptions | undefined;
  type?: ApexChartProps['type'];
};

const WEEK_TIMESTEP = 'year_week';
const MONTH_TIMESTEP = 'year_month';
const INCOMPLETE_LINE_DASH = 6;
const INCOMPLETE_BAR_OPACITY = 0.5;
const INCOMPLETE_NAME_SUFFIX = '\u200B';

const axisFriendlyTypes = new Set(['line', 'area', 'bar']);
const lineLikeTypes = new Set(['line', 'area']);
const stripIncompleteSuffix = (name?: string) =>
  typeof name === 'string' && name.endsWith(INCOMPLETE_NAME_SUFFIX)
    ? name.slice(0, -INCOMPLETE_NAME_SUFFIX.length)
    : name;

const getSafeDate = (value?: string) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const shouldMarkLastPointIncomplete = (filters?: Filters) => {
  const timestep = filters?.timestep;
  const endDate = getSafeDate(filters?.end);

  if (!endDate || (timestep !== WEEK_TIMESTEP && timestep !== MONTH_TIMESTEP)) {
    return false;
  }

  const actualDays =
    timestep === WEEK_TIMESTEP
      ? differenceInCalendarDays(endDate, startOfWeek(endDate, { weekStartsOn: 1 })) + 1
      : differenceInCalendarDays(endDate, startOfMonth(endDate)) + 1;

  const expectedDays = timestep === WEEK_TIMESTEP ? 7 : getDaysInMonth(endDate);

  return actualDays < expectedDays;
};

const isAxisSeriesArray = (
  series: ApexChartProps['series']
): series is ApexChartProps['series'] & Array<{ data?: any[]; [key: string]: any }> => {
  if (!Array.isArray(series)) return false;
  return series.every(
    (item) =>
      typeof item === 'object' && item !== null && item !== undefined && ('data' in item || 'name' in item)
  );
};

const resolveSeriesType = (seriesType?: string, chartType?: ApexChartProps['type']) => {
  const candidate = (seriesType || chartType || 'line')?.toString().toLowerCase();
  return candidate === 'column' ? 'bar' : candidate;
};

const clonePoint = (point: any) => {
  if (point == null) return point;
  if (Array.isArray(point)) return [...point];
  if (typeof point === 'object') return { ...point };
  return point;
};

const nullifyPoint = (point: any) => {
  if (point == null) return point;
  if (typeof point === 'number') return null;
  if (Array.isArray(point)) {
    if (!point.length) return point;
    const clone = [...point];
    clone[1] = null;
    return clone;
  }
  if (typeof point === 'object') {
    if (Array.isArray(point.y)) {
      return { ...point, y: point.y.map(() => null) };
    }
    return { ...point, y: null };
  }
  return null;
};

const splitDataPoints = (data: any[], isLineType: boolean = false) => {
  if (!Array.isArray(data) || data.length === 0) {
    return { complete: data, incomplete: data };
  }

  const lastIndex = data.length - 1;

  if (isLineType) {
    const isObjectFormat =
      data.length > 0 &&
      typeof data[0] === 'object' &&
      data[0] !== null &&
      !Array.isArray(data[0]);

    const completeData = data.map((point, index) => (index === lastIndex ? nullifyPoint(point) : clonePoint(point)));

    const incompleteData: any[] = [];
    if (lastIndex >= 1) {
      for (let i = 0; i < lastIndex - 1; i++) {
        incompleteData.push(isObjectFormat ? { ...data[i], y: null } : null);
      }
      incompleteData.push(clonePoint(data[lastIndex - 1]));
      incompleteData.push(clonePoint(data[lastIndex]));
    } else if (lastIndex === 0) {
      incompleteData.push(clonePoint(data[0]));
    }

    return { complete: completeData, incomplete: incompleteData };
  }

  return {
    complete: data.map((point, index) => (index === lastIndex ? nullifyPoint(point) : clonePoint(point))),
    incomplete: data.map((point, index) => (index === lastIndex ? clonePoint(point) : nullifyPoint(point))),
  };
};

const normalizeNumericOption = (value: number | number[] | undefined, targetLength: number, fallback: number) => {
  if (Array.isArray(value)) {
    if (!value.length) return Array(targetLength).fill(fallback);
    const normalized = value.slice(0, targetLength);
    while (normalized.length < targetLength) {
      normalized.push(value[value.length - 1] ?? fallback);
    }
    return normalized;
  }

  if (typeof value === 'number') {
    return Array(targetLength).fill(value);
  }

  return Array(targetLength).fill(fallback);
};

const shouldSkipSeries = (serie: AxisSeries, resolvedType: string) => {
  if (!axisFriendlyTypes.has(resolvedType)) return true;
  if (!Array.isArray(serie.data) || serie.data.length === 0) return true;
  if (typeof serie.name === 'string' && /compare/i.test(serie.name)) return true;
  return false;
};

const ensureArrayYaxis = (yaxis?: ApexOptions['yaxis']): YAxisItem[] => {
  if (Array.isArray(yaxis)) return yaxis.length > 0 ? yaxis : [{}];
  if (yaxis && typeof yaxis === 'object') return [yaxis];
  return [{}];
};

const extendAxisSeriesNames = (yaxis: YAxisItem[], nameMap: Map<string, string>): YAxisItem[] =>
  yaxis.map((axis: YAxisItem) => {
    if (!axis || typeof axis !== 'object') return axis;

    const axisWithSeries = axis as YAxisItem & { seriesName?: string | string[] };
    const seriesName = axisWithSeries.seriesName;
    if (!seriesName || nameMap.size === 0) return axisWithSeries;

    const appendMissing = (names: string[]) => {
      const extras: string[] = [];
      names.forEach((name) => {
        const mapped = typeof name === 'string' ? nameMap.get(name) : undefined;
        if (mapped && !names.includes(mapped) && !extras.includes(mapped)) {
          extras.push(mapped);
        }
      });
      return extras.length ? [...names, ...extras] : names;
    };

    if (Array.isArray(seriesName)) {
      const nextNames = appendMissing(seriesName);
      return nextNames !== seriesName ? ({ ...axisWithSeries, seriesName: nextNames } as YAxisItem) : axisWithSeries;
    }

    if (typeof seriesName === 'string') {
      const mapped = nameMap.get(seriesName);
      if (mapped) {
        return { ...axisWithSeries, seriesName: [seriesName, mapped] } as YAxisItem;
      }
    }

    return axisWithSeries;
  });

const hideLegendEntries = (chartCtx: any) => {
  const legendNodes = chartCtx?.el?.querySelectorAll?.('.apexcharts-legend-series');
  if (!legendNodes) return;

  legendNodes.forEach((node: Element) => {
    const rel = node.getAttribute('rel') || node.getAttribute('data:series');
    const seriesIndex = rel ? Number(rel) : Number.NaN;
    if (Number.isNaN(seriesIndex)) return;
    const seriesName = chartCtx?.w?.config?.series?.[seriesIndex]?.name;
    if (typeof seriesName === 'string' && seriesName.endsWith(INCOMPLETE_NAME_SUFFIX)) {
      // remove overlay entry to avoid duplicate labels
      node.parentElement?.removeChild(node);
      return;
    }
    (node as HTMLElement).style.display = '';
    (node as HTMLElement).style.pointerEvents = '';
    node.removeAttribute('aria-hidden');
  });
};

const mapCompleteSeriesIndices = (w: any): Map<string, number> => {
  const result = new Map<string, number>();
  w.config.series?.forEach((s: any, i: number) => {
    if (typeof s.name === 'string' && !s.name.endsWith(INCOMPLETE_NAME_SUFFIX)) {
      result.set(s.name, i);
    }
  });
  return result;
};

const shouldFilterIncompleteSeries = (
  series: any[],
  configSeries: any,
  i: number,
  dataPointIndex: number,
  completeIndexMap: Map<string, number>
) => {
  const { name } = configSeries;
  if (typeof name !== 'string' || !name.endsWith(INCOMPLETE_NAME_SUFFIX)) return false;

  const baseName = stripIncompleteSuffix(name);
  const completeIndex = completeIndexMap.get(baseName || '');
  const incompleteValue = series[i]?.[dataPointIndex];

  if (completeIndex !== undefined) {
    const completeValue = series[completeIndex]?.[dataPointIndex];
    const completeExists = completeValue != null;
    const incompleteIsNull = incompleteValue == null;
    return completeExists || incompleteIsNull;
  }

  return configSeries.tooltip?.enabled === false || configSeries.showInLegend === false;
};

const applySeriesFiltering = (params: any, indicesToFilter: Set<number>) => {
  const { series, w } = params;
  const keep = (i: number) => !indicesToFilter.has(i);

  return {
    ...params,
    series: Array.isArray(series) ? series.filter((_, i) => keep(i)) : series,
    w: {
      ...w,
      config: {
        ...w.config,
        series: w.config.series?.filter((_: any, i: number) => keep(i)),
        colors: w.config.colors?.filter((_: any, i: number) => keep(i)),
      },
    },
  };
};

const applyIncompleteTooltipFilter = (options: ApexOptions | undefined, nameMap: Map<string, string>) => {
  if (!options || nameMap.size === 0) return options;

  const originalTooltip = options.tooltip;
  if (!originalTooltip || typeof originalTooltip !== 'object' || !originalTooltip.custom) {
    return options;
  }

  const originalCustom = Array.isArray(originalTooltip.custom) ? undefined : originalTooltip.custom;
  if (!originalCustom) {
    return options;
  }

  const filteredCustom = (params: any) => {
    const { series, dataPointIndex, w } = params;
    const completeIndexMap = mapCompleteSeriesIndices(w);
    const indicesToFilter = new Set<number>();

    w.config.series?.forEach((configSeries: any, i: number) => {
      if (shouldFilterIncompleteSeries(series, configSeries, i, dataPointIndex, completeIndexMap)) {
        indicesToFilter.add(i);
      }
    });

    const filteredParams = applySeriesFiltering(params, indicesToFilter);
    return originalCustom(filteredParams);
  };

  return {
    ...options,
    tooltip: {
      ...originalTooltip,
      custom: filteredCustom,
    },
  };
};

const applyIncompleteLegendBehavior = (options: ApexOptions | undefined, nameMap: Map<string, string>) => {
  if (!options || nameMap.size === 0) return options;

  const originalLegend = options.legend || {};
  const originalChart = options.chart || {};
  const originalEvents = originalChart.events || {};
  const originalFormatter = originalLegend.formatter;

  const getSeriesIndexByName = (chartCtx: any, name?: string) => {
    if (!name) return -1;
    return chartCtx?.w?.config?.series?.findIndex((serie: AxisSeries) => serie?.name === name) ?? -1;
  };

  const syncOverlayVisibility = (chartCtx: any) => {
    const collapsed = chartCtx?.w?.globals?.collapsedSeriesIndices || [];
    if (!Array.isArray(collapsed)) return;

    nameMap.forEach((overlayName, baseName) => {
      const baseIdx = getSeriesIndexByName(chartCtx, baseName);
      const overlayIdx = getSeriesIndexByName(chartCtx, overlayName);
      if (baseIdx < 0 || overlayIdx < 0) return;

      const baseHidden = collapsed.includes(baseIdx);
      const overlayHidden = collapsed.includes(overlayIdx);

      if (baseHidden && !overlayHidden) {
        chartCtx.hideSeries(overlayName);
      } else if (!baseHidden && overlayHidden) {
        chartCtx.showSeries(overlayName);
      }
    });
  };

  const legendFormatter: NonNullable<ApexOptions['legend']>['formatter'] = originalFormatter
    ? (seriesName: string, opts: any) => originalFormatter(stripIncompleteSuffix(seriesName) || seriesName, opts)
    : (seriesName?: string) => stripIncompleteSuffix(seriesName) || seriesName || '';

  const legendClick = (chartCtx: any, seriesIndex: number, config: any) => {
    const isSeriesHidden = (name?: string) => {
      const idx = getSeriesIndexByName(chartCtx, name);
      if (idx < 0) return false;
      return chartCtx?.w?.globals?.collapsedSeriesIndices?.includes(idx);
    };

    const clickedSeries = config?.config?.series?.[seriesIndex];
    const rawName: string | undefined = clickedSeries?.name;
    if (!rawName) {
      originalEvents.legendClick?.(chartCtx, seriesIndex, config);
      return;
    }

    const normalizedName = stripIncompleteSuffix(rawName) || rawName;
    const overlayName = rawName.endsWith(INCOMPLETE_NAME_SUFFIX) ? rawName : nameMap.get(normalizedName);
    const baseName = rawName.endsWith(INCOMPLETE_NAME_SUFFIX) ? normalizedName : rawName;

    const shouldHide = !isSeriesHidden(baseName);

    if (shouldHide) {
      chartCtx.hideSeries(baseName);
      if (overlayName && overlayName !== baseName) {
        chartCtx.hideSeries(overlayName);
      }
    } else {
      chartCtx.showSeries(baseName);
      if (overlayName && overlayName !== baseName) {
        chartCtx.showSeries(overlayName);
      }
    }

    originalEvents.legendClick?.(chartCtx, seriesIndex, config);
  };

  const mergedEvents = {
    ...originalEvents,
    mounted: (chartCtx: any, ...rest: any[]) => {
      hideLegendEntries(chartCtx);
       syncOverlayVisibility(chartCtx);
      originalEvents.mounted?.(chartCtx, ...rest);
    },
    updated: (chartCtx: any, ...rest: any[]) => {
      hideLegendEntries(chartCtx);
       syncOverlayVisibility(chartCtx);
      originalEvents.updated?.(chartCtx, ...rest);
    },
    legendClick,
  };

  return {
    ...options,
    legend: {
      ...originalLegend,
      formatter: legendFormatter,
      onItemClick: {
        ...(originalLegend.onItemClick || {}),
        toggleDataSeries: false,
      },
    },
    chart: {
      ...originalChart,
      events: mergedEvents,
    },
  };
};

const updateOptions = (
  options: ApexOptions | undefined,
  dashArray: number[],
  fillOpacity: number[],
  needsStrokeUpdate: boolean,
  needsFillUpdate: boolean
): ApexOptions => {
  if (!needsStrokeUpdate && !needsFillUpdate) {
    return options || {};
  }

  const nextOptions: ApexOptions = { ...(options || {}) };

  if (needsStrokeUpdate) {
    nextOptions.stroke = {
      ...(options?.stroke || {}),
      dashArray,
    };
  }

  if (needsFillUpdate) {
    nextOptions.fill = {
      ...(options?.fill || {}),
      opacity: fillOpacity,
    };
  }

  return nextOptions;
};

const deriveSeriesWithIncomplete = (series: ApexChartProps['series'], options: ApexOptions | undefined, chartType?: ApexChartProps['type']): TransformResult => {
  if (!isAxisSeriesArray(series) || !Array.isArray(series)) {
    return { series, options };
  }

  const baseDashArray = normalizeNumericOption(options?.stroke?.dashArray, series.length, 0);
  const baseFillOpacity = normalizeNumericOption(options?.fill?.opacity, series.length, 1);

  const nextSeries: AxisSeries[] = [];
  const nextDashArray: number[] = [];
  const nextFillOpacity: number[] = [];
  const overlayIndices: number[] = [];
  const incompleteNameMap = new Map<string, string>();

  let hasLineOverlay = false;
  let hasBarOverlay = false;

  series.forEach((serie, index) => {
    if (typeof serie !== 'object' || serie === null || Array.isArray(serie)) {
      return;
    }

    const serieObj = serie as AxisSeries;
    const resolvedType = resolveSeriesType(serieObj.type, chartType);
    const dashValue = baseDashArray[index] ?? 0;
    const fillValue = baseFillOpacity[index] ?? 1;

    if (shouldSkipSeries(serieObj, resolvedType)) {
      nextSeries.push(serieObj);
      nextDashArray.push(dashValue);
      nextFillOpacity.push(fillValue);
      return;
    }

    const isLineType = lineLikeTypes.has(resolvedType);
    const { complete, incomplete } = splitDataPoints(serieObj.data || [], isLineType);

    if (!Array.isArray(complete) || !Array.isArray(incomplete)) {
      nextSeries.push(serieObj);
      nextDashArray.push(dashValue);
      nextFillOpacity.push(fillValue);
      return;
    }

    const serieColor = serieObj.color || options?.colors?.[index];

    const completeSeries = {
      ...serieObj,
      data: complete,
    };
    nextSeries.push(completeSeries);
    nextDashArray.push(dashValue);
    nextFillOpacity.push(fillValue);

    const colorForIncomplete =
      resolvedType === 'bar' && serieColor ? alpha(serieColor, INCOMPLETE_BAR_OPACITY) : serieColor;

    const baseOverlaySeries = {
      ...serieObj,
      name:
        typeof serieObj.name === 'string'
          ? (() => {
              const mapped = `${serieObj.name}${INCOMPLETE_NAME_SUFFIX}`;
              incompleteNameMap.set(serieObj.name, mapped);
              return mapped;
            })()
          : undefined,
      data: incomplete,
      showInLegend: false,
      id: `${serieObj.name ?? 'series'}-incomplete-${index}`,
      color: colorForIncomplete,
      markers: { size: 0 },
      stroke: {
        ...(serieObj.stroke || {}),
        width: 0,
      },
      tooltip: { enabled: false },
    };

    const overlayIndex = nextSeries.length;
    nextSeries.push(baseOverlaySeries);
    overlayIndices.push(overlayIndex);

    if (lineLikeTypes.has(resolvedType)) {
      nextDashArray.push(INCOMPLETE_LINE_DASH);
      hasLineOverlay = true;
    } else {
      nextDashArray.push(dashValue);
    }

    if (resolvedType === 'bar') {
      nextFillOpacity.push(INCOMPLETE_BAR_OPACITY);
      hasBarOverlay = true;
    } else {
      nextFillOpacity.push(fillValue);
    }
  });

  const needsStrokeUpdate = hasLineOverlay || typeof options?.stroke?.dashArray !== 'undefined';
  const needsFillUpdate = hasBarOverlay || typeof options?.fill?.opacity !== 'undefined';

  if (nextSeries.length === 0) {
    return { series, options };
  }

  const finalDashArray =
    nextDashArray.length === nextSeries.length && nextDashArray.length > 0
      ? nextDashArray
      : Array(nextSeries.length)
          .fill(0)
          .map((_, idx) => nextDashArray[idx] ?? 0);

  const finalFillOpacity =
    nextFillOpacity.length === nextSeries.length && nextFillOpacity.length > 0
      ? nextFillOpacity
      : Array(nextSeries.length)
          .fill(1)
          .map((_, idx) => nextFillOpacity[idx] ?? 1);

  const nextOptions = updateOptions(options, finalDashArray, finalFillOpacity, needsStrokeUpdate, needsFillUpdate);

  const baseYaxis = nextOptions.yaxis ?? options?.yaxis;
  const ensuredYaxis = ensureArrayYaxis(baseYaxis);
  nextOptions.yaxis = extendAxisSeriesNames(ensuredYaxis, incompleteNameMap) as ApexOptions['yaxis'];

  const duplicatePalette = (palette?: string[]) => {
    if (!Array.isArray(palette) || palette.length === 0) return undefined;
    const duplicatedColors: string[] = [];
    series.forEach((serie, index) => {
      if (typeof serie === 'object' && serie !== null && !Array.isArray(serie)) {
        const serieObj = serie as AxisSeries;
        const resolvedType = resolveSeriesType(serieObj.type, chartType);
        if (!shouldSkipSeries(serieObj, resolvedType)) {
          duplicatedColors.push(serieObj.color || palette[index] || palette[index % palette.length]);
          duplicatedColors.push(serieObj.color || palette[index] || palette[index % palette.length]);
        } else {
          duplicatedColors.push(serieObj.color || palette[index] || palette[index % palette.length]);
        }
      }
    });
    return duplicatedColors.length > 0 ? duplicatedColors : undefined;
  };

  const duplicatedStrokeColors = duplicatePalette(options?.colors);
  if (duplicatedStrokeColors) {
    nextOptions.colors = duplicatedStrokeColors;
  }

  const duplicatedFillColors = duplicatePalette(options?.fill?.colors as string[] | undefined);
  if (duplicatedFillColors) {
    nextOptions.fill = {
      ...(nextOptions.fill || options?.fill || {}),
      colors: duplicatedFillColors,
    };
  }

  if (overlayIndices.length) {
    const overlayIndexSet = new Set(overlayIndices);
    const allowedTooltipSeries = nextSeries
      .map((_, idx) => idx)
      .filter((idx) => !overlayIndexSet.has(idx));

    if (allowedTooltipSeries.length) {
      nextOptions.tooltip = {
        ...(nextOptions.tooltip || options?.tooltip || {}),
        enabledOnSeries: allowedTooltipSeries,
      };
    }
  }

  const validSeries = nextSeries.map((serie) => {
    if (!serie || typeof serie !== 'object') return serie;
    return {
      ...serie,
      data: Array.isArray(serie.data) ? serie.data : [],
    };
  });

  return {
    series: validSeries as ApexChartProps['series'],
    options: nextOptions,
    nameMap: incompleteNameMap,
  };
};

export const useIncompleteHighlight = ({
  enableIncompleteHighlight,
  filters,
  series,
  options,
  type,
}: Params): TransformResult => {
  const shouldSplit = useMemo(
    () => enableIncompleteHighlight && shouldMarkLastPointIncomplete(filters),
    [enableIncompleteHighlight, filters?.end, filters?.timestep]
  );

  const derived = useMemo(() => {
    if (!shouldSplit) {
      return { series, options, nameMap: undefined };
    }
    return deriveSeriesWithIncomplete(series, options, type);
  }, [series, options, type, shouldSplit]);

  const enhancedOptions = useMemo(() => {
    if (!enableIncompleteHighlight || !derived.nameMap?.size) {
      return derived.options;
    }
    const withLegend = applyIncompleteLegendBehavior(derived.options, derived.nameMap);
    return applyIncompleteTooltipFilter(withLegend, derived.nameMap);
  }, [derived.options, derived.nameMap, enableIncompleteHighlight]);

  return { series: derived.series, options: enhancedOptions };
};

